if (!customElements.get('armada-fullscreen-gallery')) {
  class ArmadaFullscreenGallery extends HTMLElement {
    constructor() {
      super();
      this.isPlaying = true;
      this.isVisible = false;
      this.progress = 0;
      this.animationFrame = null;
      this.duration = (parseInt(this.dataset.autoplaySpeed) || 5) * 1000;
      this.currentIndex = 0;
      this.listenersBound = false;
      this.activeMql = null;
    }

    connectedCallback() {
      this.track = this.querySelector('.armada-fsg__track');
      this.slides = this.querySelectorAll('.armada-fsg__slide');
      this.dots = this.querySelectorAll('.armada-fsg__dot');
      this.prevBtn = this.querySelector('[data-fsg-prev]');
      this.nextBtn = this.querySelector('[data-fsg-next]');
      this.pauseBtn = this.querySelector('[data-fsg-pause]');

      const query = this.dataset.activeMedia;
      if (query) {
        this.activeMql = window.matchMedia(query);
        this.onActiveMediaChange = () => this.syncActiveMedia();
        this.activeMql.addEventListener('change', this.onActiveMediaChange);
      }

      this.syncActiveMedia();
    }

    disconnectedCallback() {
      this.teardownPlayback();
      if (this.activeMql && this.onActiveMediaChange) {
        this.activeMql.removeEventListener('change', this.onActiveMediaChange);
      }
    }

    isMediaActive() {
      return !this.activeMql || this.activeMql.matches;
    }

    syncActiveMedia() {
      const active = this.isMediaActive();
      this.toggleAttribute('inert', !active);
      if (active) {
        this.removeAttribute('aria-hidden');
      } else {
        this.setAttribute('aria-hidden', 'true');
      }

      if (active) {
        this.enable();
      } else {
        this.disable();
      }
    }

    enable() {
      if (this.slides.length < 2) return;

      if (!this.listenersBound) {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.isPlaying = false;
          this.syncPauseButton();
        }
        this.setupEventListeners();
        this.listenersBound = true;
      }

      this.observeVisibility();
    }

    disable() {
      this.isVisible = false;
      this.teardownPlayback();
    }

    teardownPlayback() {
      cancelAnimationFrame(this.animationFrame);
      if (this.visibilityObserver) {
        this.visibilityObserver.disconnect();
        this.visibilityObserver = null;
      }
    }

    observeVisibility() {
      if (this.visibilityObserver) return;

      this.visibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.isVisible = entry.isIntersecting && this.isMediaActive();
          if (this.isVisible && this.isPlaying) {
            this.startProgress();
          } else {
            cancelAnimationFrame(this.animationFrame);
          }
        });
      }, { threshold: 0.15 });
      this.visibilityObserver.observe(this);
    }

    setupEventListeners() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => {
          this.goToSlide(this.currentIndex - 1);
          this.resetProgress();
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => {
          this.goToSlide(this.currentIndex + 1);
          this.resetProgress();
        });
      }

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          const index = parseInt(dot.dataset.index);
          this.goToSlide(index);
          this.resetProgress();
        });
      });

      this.track.addEventListener('scroll', () => {
        this.updateActiveFromScroll();
      }, { passive: true });

      if (this.pauseBtn) {
        this.pauseBtn.addEventListener('click', () => {
          this.togglePlay();
        });
        this.syncPauseButton();
      }
    }

    togglePlay() {
      this.isPlaying = !this.isPlaying;
      if (this.isPlaying) {
        this.resetProgress();
      } else {
        cancelAnimationFrame(this.animationFrame);
      }
      this.syncPauseButton();
    }

    syncPauseButton() {
      if (!this.pauseBtn) return;
      const paused = !this.isPlaying;
      this.pauseBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      const label = this.pauseBtn.dataset[paused ? 'labelResume' : 'labelPause'];
      if (label) this.pauseBtn.setAttribute('aria-label', label);
      const pauseIcon = this.pauseBtn.querySelector('.armada-fsg__pause-icon--pause');
      const playIcon = this.pauseBtn.querySelector('.armada-fsg__pause-icon--play');
      if (pauseIcon) pauseIcon.hidden = paused;
      if (playIcon) playIcon.hidden = !paused;
    }

    getActiveIndex() {
      let closestIndex = 0;
      let minDiff = Infinity;
      this.slides.forEach((slide, index) => {
        const diff = Math.abs(slide.offsetLeft - this.track.scrollLeft);
        if (diff < minDiff) { minDiff = diff; closestIndex = index; }
      });
      return closestIndex;
    }

    updateActiveFromScroll() {
      const newIndex = this.getActiveIndex();
      if (newIndex !== this.currentIndex) {
        this.currentIndex = newIndex;
        this.updateDots();
      }
    }

    updateDots() {
      this.dots.forEach((dot, i) => {
        const isActive = i === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
        if (!isActive) {
          const fill = dot.querySelector('.armada-fsg__dot-fill');
          if (fill) fill.style.width = '0';
        }
      });
    }

    goToSlide(index) {
      const total = this.slides.length;
      if (index < 0) index = total - 1;
      if (index >= total) index = 0;
      this.currentIndex = index;
      const slide = this.slides[index];
      this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
      this.updateDots();
    }

    startProgress() {
      let lastTime = performance.now();

      const animate = (currentTime) => {
        if (!this.isPlaying || !this.isVisible || !this.isMediaActive()) return;

        const delta = currentTime - lastTime;
        lastTime = currentTime;
        this.progress += delta;

        if (this.progress >= this.duration) {
          this.progress = 0;
          this.goToSlide(this.currentIndex + 1);
        }

        const pct = Math.min((this.progress / this.duration) * 100, 100);
        const activeDot = this.dots[this.currentIndex];
        if (activeDot) {
          const fill = activeDot.querySelector('.armada-fsg__dot-fill');
          if (fill) fill.style.width = `${pct}%`;
        }

        this.animationFrame = requestAnimationFrame(animate);
      };

      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = requestAnimationFrame(animate);
    }

    resetProgress() {
      this.progress = 0;
      this.dots.forEach((dot) => {
        const fill = dot.querySelector('.armada-fsg__dot-fill');
        if (fill) fill.style.width = '0';
      });
      if (this.isPlaying && this.isVisible && this.isMediaActive()) {
        this.startProgress();
      }
    }
  }

  customElements.define('armada-fullscreen-gallery', ArmadaFullscreenGallery);
}
